import type { Auth0Client, LoginOpts } from './types/auth0Client';
import type { LoginAppState } from './types';
import { trackUserLoginEvent } from './reporting';
import type { LoginReason } from '@tv2no/tv2no-snowplow-tracking';
import { isLoginReason } from './utils';

export const setupClient = (): Promise<Auth0Client> => {
  const { callbackUrl, domain, clientId, userApiUrl } = window.AUTH0_CONFIG;

  if (!window.auth0) return Promise.reject('auth0 script not loaded');

  return window.auth0
    .createAuth0Client({
      domain,
      clientId,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: {
        audience: `https://${domain}`,
        redirect_uri: `${callbackUrl}?internalUrl=${encodeURIComponent(location.href.toString())}`,
      },
    })
    .then(auth0Client => {
      const login = async function (arg1?: string | LoginReason, arg2?: string) {
        if (isLoginReason(arg1)) {
          const reason = arg1;
          const appStatePayload: LoginAppState = { ...reason };
          const opts: LoginOpts = {
            appState: appStatePayload,
            authorizationParams: {},
          };

          await auth0Client.loginWithRedirect(opts);
        } else {
          const opts: LoginOpts = {
            authorizationParams: {},
          };
          const reasonToLoginCategory = typeof arg1 === 'string' ? arg1 : undefined;
          const reasonToLoginName = arg2;
          if (reasonToLoginCategory) {
            opts.authorizationParams.reasonToLoginCategory = reasonToLoginCategory;
          }
          if (reasonToLoginName) {
            opts.authorizationParams.reasonToLoginName = reasonToLoginName;
          }

          await auth0Client.loginWithRedirect(opts);
        }
      };

      const logout = async function () {
        const user = await window?.TV2?.getLoggedInUser();
        const userId = user?.id;

        trackUserLoginEvent('Logout Clicked', userId);

        // Clear coral tokens from localStorage
        localStorage.removeItem('coral_token');
        localStorage.removeItem('coral_token_expiry');

        await auth0Client.logout({
          logoutParams: {
            returnTo: `${callbackUrl}?internalUrl=${encodeURIComponent(location.href.toString())}&logout=true`,
          },
        });
      };

      const isAuthenticated = async function () {
        return await auth0Client.isAuthenticated();
      };

      const getLoggedInUser = async function () {
        try {
          const token = await auth0Client.getTokenSilently({ authorizationParams: { redirect_uri: location.origin } });

          if (!token) return;

          const userResponse = await fetch(userApiUrl, {
            method: 'get',
            credentials: 'include',
            headers: new Headers({
              Authorization: 'Bearer ' + token,
            }),
          });

          const userData = await userResponse.json();

          return userData;
        } catch (e) {
          return;
        }
      };

      const getToken = async function () {
        return await auth0Client.getTokenSilently({ authorizationParams: { redirect_uri: location.origin } });
      };

      window.TV2 = { ...window.TV2, login, logout, isAuthenticated, getLoggedInUser, getToken };

      return auth0Client;
    });
};
